import { Facebook, Instagram, Youtube } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { scrollToId } from "@/lib/site";

const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    body: "Sree Prathama Ayurvedam collects only the information you submit through the consultation request form (parent name, contact details and child-related preferences). This information is used solely to contact you regarding your consultation request and appointment coordination. We do not sell, rent or share your personal information with third parties for marketing purposes. You may request deletion of your information at any time by contacting our team.",
  },
  terms: {
    title: "Terms & Conditions",
    body: "Consultation requests submitted through this website are not confirmed appointments. All appointments are scheduled and confirmed by the USA consultation team, subject to availability during the visit period. Consultation fees, locations, dates and timings will be communicated directly by the team. Content on this website is provided for general information and does not constitute medical advice.",
  },
  disclaimer: {
    title: "Medical Disclaimer",
    body: "Ayurvedic consultations offered by Sree Prathama Ayurvedam are complementary and personalized. They are not a replacement for appropriate medical, developmental, therapeutic or emergency care, and are not intended to diagnose, treat, cure or prevent any disease. Always continue care with your child's licensed physicians, therapists and specialists, and consult them before making changes to existing treatment plans.",
  },
};

const LINKS = [
  { label: "About Dr. Santhisree", id: "doctor" },
  { label: "Consultation Pathways", id: "packages" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "lead-form" },
];

export default function Footer() {
  return (
    <footer id="footer" data-testid="site-footer" className="bg-[#132C20] border-t border-[#C5A059]/15 text-[#FAF7F2]/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid md:grid-cols-3 gap-10">
        <div>
          <p className="font-serif text-2xl text-[#FAF7F2]">Sree Prathama</p>
          <p className="text-[10px] font-semibold tracking-[0.42em] uppercase text-[#C5A059] mt-1">Ayurvedam</p>
          <p className="mt-4 font-serif italic text-[#FAF7F2]/60">Holistic Healing. Brighter Tomorrows.</p>
          <div className="mt-6 flex gap-3">
            {[
              { icon: Facebook, label: "facebook" },
              { icon: Instagram, label: "instagram" },
              { icon: Youtube, label: "youtube" },
            ].map((s) => (
              <a
                key={s.label}
                data-testid={`social-${s.label}`}
                href="#"
                onClick={(e) => e.preventDefault()}
                aria-label={s.label}
                className="w-10 h-10 rounded-full border border-[#FAF7F2]/15 flex items-center justify-center hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
              >
                <s.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold tracking-[0.24em] uppercase text-[#C5A059]">Explore</p>
          <ul className="mt-5 space-y-3">
            {LINKS.map((l) => (
              <li key={l.id}>
                <button
                  data-testid={`footer-link-${l.id}`}
                  onClick={() => scrollToId(l.id, `footer_${l.id}`)}
                  className="text-sm hover:text-[#C5A059] transition-colors"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs font-semibold tracking-[0.24em] uppercase text-[#C5A059]">Legal</p>
          <ul className="mt-5 space-y-3">
            {Object.entries(LEGAL).map(([key, doc]) => (
              <li key={key}>
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      id={`footer-${key === "disclaimer" ? "disclaimer" : key}-btn`}
                      data-testid={`footer-${key}-btn`}
                      className="text-sm hover:text-[#C5A059] transition-colors"
                    >
                      {doc.title}
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#FAF7F2] border-[#E5D3AD] max-w-lg" data-testid={`legal-dialog-${key}`}>
                    <DialogHeader>
                      <DialogTitle className="font-serif text-2xl text-[#1B3B2B]">{doc.title}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-[#5C6E62] leading-relaxed">{doc.body}</p>
                  </DialogContent>
                </Dialog>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-[#FAF7F2]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row gap-3 items-center justify-between text-[11px] text-[#FAF7F2]/40">
          <p>© {new Date().getFullYear()} Sree Prathama Ayurvedam. All rights reserved.</p>
          <p className="text-center sm:text-right max-w-xl">
            Ayurvedic consultations are complementary and personalized. They are not a replacement for appropriate
            medical, developmental, therapeutic or emergency care.
          </p>
        </div>
      </div>
    </footer>
  );
}
